import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import * as brandIcons from '@fortawesome/free-brands-svg-icons';
import { useBusinessData } from '../../context/BusinessContext';
import emailjs from '@emailjs/browser';
import '../../css/Contact.css';
import SocialMediaLinks from '../common/SocialMediaLinks';

const Contact = () => {
  const { loading, error, getData, hasData, getImageUrl } = useBusinessData();
  const form = useRef();
  
  // Get all social media links dynamically
  const getSocialMediaLinks = () => {
    const socialMedia = getData('contactInfo.socialMedia', {});
    return Object.entries(socialMedia)
      .filter(([key, socialData]) => 
        socialData && 
        socialData.url && 
        socialData.url.trim() !== '' && 
        socialData.url !== 'https://chat.whatsapp.com/YOUR_COMMUNITY_INVITE_LINK' &&
        (socialData.enabled !== false) // Check enabled flag (default true if undefined)
      )
      .map(([key, socialData]) => ({
        key,
        url: socialData.url,
        icon: brandIcons[socialData.icon] || solidIcons.faLink,
        label: socialData.label || key.charAt(0).toUpperCase() + key.slice(1)
      }));
  };
  
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    subject: '',
    message: ''
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    // Send email using EmailJS
    emailjs.sendForm(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      form.current,
      process.env.REACT_APP_EMAILJS_USER_ID
    )
    .then((result) => {
      console.log('Email sent successfully:', result.text);
      setFormSubmitted(true);
      setIsSubmitting(false);
      
      // Reset form after submission
      setFormData({
        user_name: '',
        user_email: '',
        user_phone: '',
        subject: '',
        message: ''
      });
      
      // Reset submission status after 5 seconds
      setTimeout(() => {
        setFormSubmitted(false);
      }, 5000);
    })
    .catch((error) => {
      console.error('Failed to send email:', error);
      setSubmitError('Failed to send your message. Please try again later.');
      setIsSubmitting(false);
    });
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error loading data: {error}</div>;
  }
  
  // Get contact hero background image from JSON using getImageUrl
  const contactHeroImage = getImageUrl('images.backgrounds.contact', 'contact-hero.jpg');
  
  // Create inline style for background image - using URL directly
  const contactHeroStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('${contactHeroImage}')`
  };

  return (
    <>
      {/* Contact Hero */}
      <div className="contact-hero" style={contactHeroStyle}>
        <div className="container">
          <div className="contact-hero-content">
            <h1>{getData('ui.contact.pageTitle', 'Contact Us')}</h1>
            <p>{getData('ui.contact.pageSubtitle', 'Reach out to us for any inquiries or to schedule a consultation')}</p>
          </div>
        </div>
      </div>
      
      {/* Contact Information */}
      <section className="section">
        <div className="container">
          <div className="contact-container">
            <div className="contact-info">
              <h2>{getData('ui.contact.infoSection.title', 'Get in Touch')}</h2>
              <p>
                {getData('ui.contact.infoSection.subtitle', 'Have a question or need more information about our services? Our team is here to help. Fill out the form or use the contact details below to get in touch.')}
              </p>
              
              {/* Address */}
              {hasData('contactInfo.address') && (
                <div className="info-item">
                  <FontAwesomeIcon icon={solidIcons.faMapMarkerAlt} className="info-icon" />
                  <div>
                    <h3>{getData('ui.contact.locationLabel', 'Our Location')}</h3>
                    <p>{getData('contactInfo.address.street', '')}, {getData('contactInfo.address.city', '')}, {getData('contactInfo.address.state', '')} {getData('contactInfo.address.zipCode', '')}</p>
                  </div>
                </div>
              )}
              
              {/* Phone */}
              {hasData('contactInfo.phone') && (
                <div className="info-item">
                  <FontAwesomeIcon icon={solidIcons.faPhone} className="info-icon" />
                  <div>
                    <h3>{getData('ui.contact.phoneLabel', 'Phone Number')}</h3>
                    <p>{getData('contactInfo.phone')}</p>
                  </div>
                </div>
              )}
              
              {/* Email */}
              {hasData('contactInfo.email') && (
                <div className="info-item">
                  <FontAwesomeIcon icon={solidIcons.faEnvelope} className="info-icon" />
                  <div>
                    <h3>{getData('ui.contact.emailLabel', 'Email Address')}</h3>
                    <p>{getData('contactInfo.email')}</p>
                  </div>
                </div>
              )}
              
              {/* Business Hours */}
              {hasData('contactInfo.businessHours') && (
                <div className="info-item">
                  <FontAwesomeIcon icon={solidIcons.faClock} className="info-icon" />
                  <div>
                    <h3>{getData('ui.contact.hoursLabel', 'Business Hours')}</h3>
                    <p>Monday - Friday: {getData('contactInfo.businessHours.weekdays')}</p>
                    <p>Saturday - Sunday: {getData('contactInfo.businessHours.weekends')}</p>
                  </div>
                </div>
              )}
              
              {/* Social Media Links */}
              {getSocialMediaLinks().length > 0 && <SocialMediaLinks variant="contact" />}
            </div>
            
            <div className="contact-form-container">
              <h2>{getData('ui.contact.formSection.title', 'Send Us a Message')}</h2>
              
              {formSubmitted && (
                <div className="form-success">
                  <p>{getData('ui.contact.formSection.successMessage', 'Thank you for your message! We\'ll get back to you shortly.')}</p>
                </div>
              )}
              
              {submitError && (
                <div className="form-error">
                  <p>{submitError}</p>
                </div>
              )}
              
              <form className="contact-form" ref={form} onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="user_name">Your Name</label>
                  <input
                    type="text"
                    id="user_name"
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="user_email">Email Address</label>
                  <input
                    type="email"
                    id="user_email"
                    name="user_email"
                    value={formData.user_email}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="user_phone">Phone Number</label>
                  <input
                    type="tel"
                    id="user_phone"
                    name="user_phone"
                    value={formData.user_phone}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Your Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    required
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : getData('ui.global.submitButton', 'Send Message')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      
      {/* Google Map */}
      {hasData('location') && getData('location.enabled') !== false && (
        <section className="map-section">
          <div className="map-container">
            <div className="map-placeholder">
              <p>Google Map would be embedded here in a production environment</p>
              <p>Coordinates: {getData('location.lat')}, {getData('location.lng')}</p>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Contact; 